import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Pencil, Plus, Trash2 } from "lucide-react";
import ModalShell from "../modal/ModalShell";
import AnnouncementMessageFields from "./AnnouncementMessageFields";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MessageTemplateResponse } from "../../features/announcements/contracts/MessageTemplateResponse";
import {
  selectMessageTemplates,
  selectMessageTemplatesStatus,
} from "../../features/announcements/announcements-slice";
import {
  createMessageTemplate,
  deleteMessageTemplate,
  fetchMessageTemplates,
  updateMessageTemplate,
} from "../../features/announcements/slice-extensions/announcements-slice-extension";

interface Props {
  onClose: () => void;
}

/** Standalone template management: list, create, edit and delete — no send required. */
const ManageTemplatesDialog = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const templates = useAppSelector(selectMessageTemplates);
  const templatesStatus = useAppSelector(selectMessageTemplatesStatus);

  // null = list view; "new" = create form; otherwise the template being edited.
  const [editing, setEditing] = useState<MessageTemplateResponse | "new" | null>(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dispatch(fetchMessageTemplates());
  }, [dispatch]);

  const openForm = (template: MessageTemplateResponse | "new") => {
    setEditing(template);
    setName(template === "new" ? "" : template.name);
    setSubject(template === "new" ? "" : template.subject);
    setBody(template === "new" ? "" : template.body);
  };

  const canSave = name.trim().length > 0 && subject.trim().length > 0 && body.trim().length > 0;

  const handleSave = async () => {
    if (!canSave || editing === null) return;
    setBusy(true);
    const payload = { name: name.trim(), subject: subject.trim(), body: body.trim() };
    try {
      if (editing === "new") {
        await dispatch(createMessageTemplate(payload)).unwrap();
      } else {
        await dispatch(updateMessageTemplate({ id: editing.id, request: payload })).unwrap();
      }
      toast.success(t("announcements.templateSaved", "Template saved"));
      setEditing(null);
    } catch (error) {
      toast.error(
        (error as { message?: string })?.message ||
          t("announcements.templateSaveFailed", "Saving failed"),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell onClose={onClose} labelledBy="manage-templates-title">
      <div className="p-6">
        <h2 id="manage-templates-title" className="text-lg font-bold text-grey-20 dark:text-white">
          {t("announcements.manageTemplates", "Templates")}
        </h2>
        <p className="mt-1 text-sm text-grey-50 dark:text-grey-70">
          {t(
            "announcements.manageTemplatesSubtitle",
            "Reusable starting points for announcements — lesson cancelled, term dates, price change.",
          )}
        </p>

        {editing === null && (
          <>
            {templatesStatus === "loading" && templates.length === 0 && (
              <p className="mt-4 text-sm text-grey-40 dark:text-grey-80">
                {t("common.loading", "Loading…")}
              </p>
            )}

            {templatesStatus !== "loading" && templates.length === 0 && (
              <p className="mt-4 text-sm text-grey-40 dark:text-grey-80">
                {t("announcements.noTemplates", "No templates yet. Create your first one.")}
              </p>
            )}

            <ul className="mt-4 flex flex-col gap-2">
              {templates.map((template) => (
                <li
                  key={template.id}
                  className="with-border flex items-start justify-between gap-3 rounded-xl bg-white p-3 dark:bg-primary-20"
                >
                  <div className="min-w-0">
                    <p className="font-medium text-grey-20 dark:text-white">{template.name}</p>
                    <p className="truncate text-sm text-grey-40 dark:text-grey-80">
                      {template.subject}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      aria-label={t("announcements.editTemplate", "Edit template")}
                      title={t("announcements.editTemplate", "Edit template")}
                      className="text-grey-40 hover:text-primary-40 dark:text-grey-80 dark:hover:text-primary-90"
                      onClick={() => openForm(template)}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      type="button"
                      aria-label={t("announcements.deleteTemplate", "Delete template")}
                      title={t("announcements.deleteTemplate", "Delete template")}
                      className="text-grey-40 hover:text-red-700 dark:text-grey-80 dark:hover:text-red-400"
                      onClick={() => dispatch(deleteMessageTemplate(template.id))}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex justify-between gap-2">
              <button type="button" className="btn btn-secondary" onClick={() => openForm("new")}>
                <Plus size={16} />
                {t("announcements.newTemplate", "New template")}
              </button>
              <button type="button" className="btn btn-primary" onClick={onClose}>
                {t("common.done", "Done")}
              </button>
            </div>
          </>
        )}

        {editing !== null && (
          <>
            <label className="mt-4 block text-sm font-medium text-grey-30 dark:text-grey-70">
              {t("announcements.templateNameLabel", "Name")}
              <input
                type="text"
                className="input mt-1"
                value={name}
                maxLength={100}
                onChange={(event) => setName(event.target.value)}
              />
            </label>

            <AnnouncementMessageFields
              subject={subject}
              body={body}
              onSubjectChange={setSubject}
              onBodyChange={setBody}
              disabled={busy}
            />

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={busy}
                onClick={() => setEditing(null)}
              >
                {t("common.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={!canSave || busy}
                onClick={handleSave}
              >
                {busy
                  ? t("announcements.savingTemplate", "Saving…")
                  : t("announcements.saveTemplate", "Save template")}
              </button>
            </div>
          </>
        )}
      </div>
    </ModalShell>
  );
};

export default ManageTemplatesDialog;
